import { z } from "zod";

/**
 * SÖZLEŞME İNCELEME AKIŞI (CLOS-155 `[148]`).
 *
 * İnceleme, belirli bir DEĞİŞMEZ versiyonun erişimi olan aktif iç kullanıcılara
 * okunmak üzere atanmasıdır. Bir karar değildir: onay, mutabakat ve imza ayrı
 * akışlardır ve bu akış onları tetiklemez.
 */

/** Bir akışa aynı anda atanabilecek azami inceleyici — konfigden gelir, koddan değil. */
export const contractReviewReminderThresholdSchema = z.object({
  /**
   * Eşiğin ANAHTARI — gönderim defterine bu yazılıyor. Gün sayısı değişse bile "bu eşik
   * için hatırlattık" gerçeği korunmalı (CLOS-156 `[149]`).
   */
  key: z.string().trim().min(1),
  /**
   * Son tarihe kalan gün. NEGATİF değer gecikmeyi anlatıyor: `-1` "son tarihten bir gün
   * sonra". Ayrı bir `overdue` bayrağı yerine tek eksende ölçmek, eşiklerin sırasını da
   * kendiliğinden veriyor.
   */
  daysBeforeDue: z.number().int(),
});

export const contractReviewConfigSchema = z.object({
  version: z.literal(3),
  /** Tek akışta en fazla kaç kişi: sınırsız liste, bildirim ve görev seli üretir. */
  maxReviewers: z.number().int().min(1),
  /** Son tarih en fazla kaç gün ilerisi olabilir. */
  maxDueDays: z.number().int().min(1),
  /** Açıklama alanının azami uzunluğu. */
  maxDescriptionLength: z.number().int().min(1),
  /**
   * Hatırlatma eşikleri (CLOS-156 `[149]` AÇIK KARARI: *"hatırlatma eşikleri, toplama ve
   * tekrar aralıkları konfigüre edilir"*). Son tarihi OLMAYAN görev hatırlatılmıyor —
   * neyin yaklaştığını söyleyemeyen bir hatırlatma gürültüdür.
   */
  reminderThresholds: z.array(contractReviewReminderThresholdSchema).min(1),
  /** Worker'ın koşu aralığı (dakika) — cron ifadesi bundan üretiliyor. */
  reminderWorkerIntervalMinutes: z.number().int().min(1).max(59),
  /**
   * Karar politikası (CLOS-157 `[150]` AÇIK KARARI: *"'değişiklik gerekli' gerekçe
   * zorunluluğu ve minimum açıklama politikası konfigüre edilir"*).
   *
   * ⚠ İki ayrı alan: gerekçenin ZORUNLU olup olmadığı ile YAZILDIĞINDA ne kadar uzun
   * olacağı farklı kararlar. Tek alana indirseydik "zorunlu değil ama yazarsan 20 karakter"
   * politikası ifade edilemezdi.
   */
  changesRequestedRequiresNote: z.boolean(),
  decisionNoteMinLength: z.number().int().min(0),
});
export type ContractReviewConfig = z.infer<typeof contractReviewConfigSchema>;

export const startContractReviewSchema = z.strictObject({
  /**
   * ⚠ Versiyon AÇIKÇA seçiliyor, "güncel" varsayılmıyor. Sunucu güncel versiyonu kendi
   * çözseydi, kullanıcının ekranda okuduğu metin ile incelemeye giden metin ayrışabilirdi
   * (kart: *"kaynak sözleşme ve versiyon CLOS-133 kimliğiyle sabitlenir"*).
   */
  versionId: z.uuid("Geçerli bir sözleşme versiyonu seçilmelidir"),
  /** En az bir inceleyici: kimseye atanmamış bir inceleme akışı bir iş değildir. */
  reviewerUserIds: z
    .array(z.uuid("Geçerli bir kullanıcı seçilmelidir"))
    .min(1, "En az bir inceleyici seçilmelidir"),
  /**
   * ISO tarih; sunucu geçmiş tarihi ve konfig tavanını ayrıca doğrular.
   *
   * ⚠ Son tarih ZORUNLU (ADR-0065, Rapor 5 §5) — süreç ölçülebilir gecikme taşımak
   * zorunda; eski satırlar null kalır.
   */
  dueDate: z.iso.datetime({ message: "Geçerli bir son tarih girin" }),
  description: z.string().trim().max(2000, "Açıklama en fazla 2000 karakter olabilir").optional(),
});
export type StartContractReviewInput = z.infer<typeof startContractReviewSchema>;

export const CONTRACT_REVIEW_STATUSES = [
  "open",
  "cancelled",
  "completed",
  "changes_requested",
] as const;
export type ContractReviewStatus = (typeof CONTRACT_REVIEW_STATUSES)[number];

export const CONTRACT_REVIEW_STATUS_NAMES: Record<ContractReviewStatus, string> = {
  open: "Devam ediyor",
  cancelled: "İptal edildi",
  completed: "İnceleme tamamlandı",
  changes_requested: "Değişiklik gerekli",
};

/**
 * İnceleyicinin verebileceği KARAR (CLOS-157 `[150]`).
 *
 * ⚠ İki değer ve ikisi de TERMİNAL: "tamamlandı" ile "değişiklik gerekli" arasında bir ara
 * durum yok. Kart *"tek kez kaydetmesi"* diyor — kararı geri alınabilir yapsaydık, akışın
 * hangi anda ne söylediği zamanla değişirdi.
 */
export const CONTRACT_REVIEW_DECISIONS = ["completed", "changes_requested"] as const;
export type ContractReviewDecision = (typeof CONTRACT_REVIEW_DECISIONS)[number];

export const decideContractReviewTaskSchema = z.strictObject({
  decision: z.enum(CONTRACT_REVIEW_DECISIONS, { message: "Geçerli bir karar seçilmelidir" }),
  /** Gerekçenin zorunluluğu ve asgari uzunluğu SUNUCUDA, konfigden doğrulanıyor. */
  note: z.string().trim().max(2000, "Gerekçe en fazla 2000 karakter olabilir").optional(),
});
export type DecideContractReviewTaskInput = z.infer<typeof decideContractReviewTaskSchema>;

/** ⚠ `reassigned` CLOS-233 `[170]` DEV-174 ile eklendi — `cancelled`'dan AYRI (bkz. şema). */
export const CONTRACT_REVIEW_TASK_STATUSES = [
  "pending",
  "cancelled",
  "completed",
  "changes_requested",
  "reassigned",
] as const;
export type ContractReviewTaskStatus = (typeof CONTRACT_REVIEW_TASK_STATUSES)[number];

/** Kullanıcıya görünen adlar ŞEMADA: arayüz kendi sözlüğünü uydurmuyor (kriter 4). */
export const CONTRACT_REVIEW_TASK_STATUS_NAMES: Record<ContractReviewTaskStatus, string> = {
  pending: "Bekliyor",
  cancelled: "İptal edildi",
  completed: "İncelendi",
  changes_requested: "Değişiklik istendi",
  reassigned: "Başka bir inceleyiciye devredildi",
};

export type ContractReviewTaskSummary = {
  id: string;
  reviewerUserId: string;
  reviewerName: string;
  position: number;
  status: ContractReviewTaskStatus;
  /** Karar gerekçesi — yalnız sonuçlanmış görevlerde dolu. */
  decisionNote: string | null;
  resolvedAt: string | null;
  /** `reassigned` durumundaki görevde dolu — yeni görevin kimliği (CLOS-922/CLOS-923). */
  reassignedToTaskId: string | null;
};

/** Görev iptali (CLOS-233 `[170]` DEV-174 — CLOS-922 ortak çekirdek/CLOS-923 adapter). */
export const cancelContractReviewTaskSchema = z.strictObject({
  reason: z.string().trim().max(2000, "Gerekçe en fazla 2000 karakter olabilir").optional(),
});
export type CancelContractReviewTaskInput = z.infer<typeof cancelContractReviewTaskSchema>;

/** Görev yeniden atama — yeni inceleyici kimliği zorunlu. */
export const reassignContractReviewTaskSchema = z.strictObject({
  newReviewerUserId: z.uuid("Geçerli bir kullanıcı seçilmelidir"),
  reason: z.string().trim().max(2000, "Gerekçe en fazla 2000 karakter olabilir").optional(),
});
export type ReassignContractReviewTaskInput = z.infer<typeof reassignContractReviewTaskSchema>;

export type ContractReviewSummary = {
  id: string;
  contractId: string;
  versionId: string;
  versionNumber: number;
  status: ContractReviewStatus;
  dueDate: string | null;
  description: string | null;
  startedByName: string;
  createdAt: string;
  tasks: ContractReviewTaskSummary[];
};
